import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import '../styles/ContactPage.css';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EMAILJS_CONFIG, EMAIL_TEMPLATE_PARAMS } from '../config/emailjs';
import ScrollAnimation from '../components/ScrollAnimation';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    company: '',
    salutation: '',
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    reason: '',
    subject: '',
    message: '',
    privacy: false
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Helper function to get CSS classes for form fields
  const getFieldClasses = (fieldName: string, isRequired: boolean = false) => {
    const classes = [];
    
    if (errors[fieldName]) {
      classes.push('error');
    }
    
    // Only show required-invalid styling after user has attempted to submit
    if (isRequired && hasAttemptedSubmit) {
      const fieldValue = formData[fieldName as keyof typeof formData];
      if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
        classes.push('required-invalid');
      }
    }
    
    return classes.join(' ');
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('lastNameRequired');
    }
    if (!formData.reason) {
      newErrors.reason = t('reasonRequired');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('messageRequired');
    }
    if (!formData.privacy) {
      newErrors.privacy = t('privacyRequired');
    }
    if (!formData.telephone) {
      newErrors.telephone = t('telephoneRequired');
    }

     // Email validation
    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set that user has attempted to submit
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      // Prepare template parameters
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        company: formData.company || 'Nicht angegeben',
        salutation: formData.salutation || 'Nicht angegeben',
        title: formData.title || 'Nicht angegeben',
        first_name: formData.firstName,
        last_name: formData.lastName,
        telephone: formData.telephone,
        reason: formData.reason,
        subject: formData.subject,
        message: formData.message,
        to_email: EMAIL_TEMPLATE_PARAMS.to_email,
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      setIsSuccess(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        resetForm();
      }, 3000);
      
    } catch (error) {
      console.error('Email sending failed:', error);
      setErrorMessage(t('emailSendError'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company: '',
      salutation: '',
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      telephone: '',
      reason: '',
      subject: '',
      message: '',
      privacy: false
    });
    setErrors({});
    setIsSuccess(false);
    setErrorMessage('');
    setHasAttemptedSubmit(false);
  };

  return (
    <div className='contact-page'>
      <div className='contact-container'>
        <ScrollAnimation animation="fadeInUp" delay={0.1}>
          <div className='form-header'>
            <h1>{t('contactPageH1')}</h1>
            <p>{t('contactPageP')}</p>
          </div>
        </ScrollAnimation>

        <ScrollAnimation animation="fadeInUp" delay={0.1}>
          <form onSubmit={handleSubmit} className='contact-form'>
            {/* Company Field */}
            <div className='form-group'>
              <input
                type='text'
                id='company'
                name='company'
                value={formData.company}
                onChange={handleChange}
                placeholder={t('companyPlaceholder')}
                className={errors.company ? 'error' : ''}
              />
              {errors.company && <span className='error-message'>{errors.company}</span>}
            </div>

            {/* Salutation and Title Row */}
            <div className='form-row'>
              <div className='form-group'>
                <select
                  id='salutation'
                  name='salutation'
                  value={formData.salutation}
                  onChange={handleChange}
                  required
                  className={formData.salutation ? 'has-value' : 'placeholder'}
                >
                  <option value=''>{t('salutationPlaceholder')}</option>
                  <option value='Mr.'>{t('salutationMr')}</option>
                  <option value='Ms.'>{t('salutationMs')}</option>
                  <option value='Divers'>{t('salutationDivers')}</option>
                </select>
              </div>
              <div className='form-group'>
                <select
                  id='title'
                  name='title'
                  value={formData.title}
                  onChange={handleChange}
                  className={formData.title ? 'has-value' : 'placeholder'}
                >
                  <option value=''>{t('titlePlaceholder')}</option>
                  <option value='Dr.'>{t('titleDr')}</option>
                  <option value='Prof.'>{t('titleProf')}</option>
                </select>
              </div>
            </div>

            {/* First Name and Last Name Row */}
            <div className='form-row'>
              <div className='form-group'>
                <input
                  type='text'
                  id='firstName'
                  name='firstName'
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder={t('firstNamePlaceholder')}
                  className={getFieldClasses('firstName', true)}
                  minLength={1}
                />
                {errors.firstName && <span className='error-message'>{errors.firstName}</span>}
              </div>
              <div className='form-group'>
                <input
                  type='text'
                  id='lastName'
                  name='lastName'
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder={t('lastNamePlaceholder')}
                  className={getFieldClasses('lastName', true)}
                  minLength={1}
                />
                {errors.lastName && <span className='error-message'>{errors.lastName}</span>}
              </div>
            </div>

            {/* Email and telephone Row */}
            <div className='form-row'>
              <div className='form-group'>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('emailPlaceholder')}
                  className={getFieldClasses('email', true)}
                />
                {errors.email && <span className='error-message'>{errors.email}</span>}
              </div>

              <div className='form-group'>
                <input
                  type='text'
                  id='telephone'
                  name='telephone'
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  placeholder={t('telephonePlaceholder')}
                  className={errors.telephone ? 'error' : ''}
                />
                {errors.telephone && <span className='error-message'>{errors.telephone}</span>}
              </div>
            </div>
              
            {/* Reason Field */}
            <div className='form-group'>
              <select
                id='reason'
                name='reason'
                value={formData.reason}
                onChange={handleChange}
                required
                className={`${formData.reason ? 'has-value' : 'placeholder'} ${getFieldClasses('reason', true)}`}
              >
                <option value=''>{t('reasonPlaceholder')}</option>
                <option value='cooperation-inquiry'>{t('reasonCooperation')}</option>
                <option value='investment-offers'>{t('reasonInvestment')}</option>
                <option value='acquisition'>{t('reasonAcquisition')}</option>
                <option value='general-inquiry'>{t('reasonGeneral')}</option>
              </select>
              {errors.reason && <span className='error-message'>{errors.reason}</span>}
            </div>

            {/* Subject Field */}
            <div className='form-group'>
              <input
                type='text'
                id='subject'
                name='subject'
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder={t('subjectPlaceholder')}
                className={getFieldClasses('subject', true)}
                minLength={1}
              />
              {errors.subject && <span className='error-message'>{errors.subject}</span>}
            </div>

            {/* Message Field */}
            <div className='form-group'>
              <textarea
                id='message'
                name='message'
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                placeholder={t('messagePlaceholder')}
                className={getFieldClasses('message', true)}
                minLength={1}
              />
              {errors.message && <span className='error-message'>{errors.message}</span>}
            </div>

            {/* Submit Button and Privacy Checkbox Row */}
            <div className='form-row-submit'>
              <div className='privacy-section'>
                <h3 className='datenschutz-header'>{t('privacyHeader')}<span className='required-asterisk'>*</span></h3>
                <div className='privacy-checkbox'>
                  <input
                    type='checkbox'
                    id='privacy'
                    name='privacy'
                    checked={formData.privacy}
                    onChange={handleChange}
                    required
                    className={errors.privacy ? 'error' : ''}
                  />
                  <label htmlFor='privacy'>
                    {t('privacyText1')} <Link to="/datenschutz">{t('privacyText2')}</Link> {t('privacyText3')}
                  </label>
                </div>
                {errors.privacy && <span className='error-message'>{errors.privacy}</span>}
              </div>
              <motion.button
                type='submit'
                className='submit-btn'
                disabled={isLoading}
                whileHover={!isLoading ? { scale: 1.05 } : {}}
                whileTap={!isLoading ? { scale: 0.95 } : {}}
              >
                {isLoading ? t('sendMessageWaiting') : t('sendMessage')}
              </motion.button>
            </div>
          </form>
        </ScrollAnimation>
      </div>

      {/* Success Message */}
      {isSuccess && (
        <div className="success-message">
          <div className="success-content">
            <h3>{t('successMessageHeader')}</h3>
            <p>{t('successMessage')}</p>
            <button onClick={resetForm} className="close-success-btn">
              {t('closeButton')}
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="error-message-modal">
          <div className="error-content">
            <h3>{t('errorMessageHeader')}</h3>
            <p>{errorMessage}</p>
            <button onClick={() => setErrorMessage('')} className="close-error-btn">
              {t('closeButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPage;